import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import capitalize from "lodash/capitalize";
import { Box, Typography } from "@mui/material";

import "./Reports.scss";
import * as reportsApi from "../../api/reports";
import Filters, { FilterProps } from "./Filters";
import { rupeeDisplay } from "../../helpers/rupee";
import { t, tl } from "../../components/translate";
import { notificationAdd } from "../../actions/notification";
import * as calFns from "../../components/Calendar/functions/calendarFunctions";
import List, { EmptyView } from "../../components/OList";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import useMobileScreen from "../../hooks/useMobileScreen";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import { RootState } from "../../store";

export interface SalesByServiceProviderReportData {
  serviceProviderId: number;
  totalSales: number;
  billCount: number;
  serviceProvider: {
    id: number;
    firstName: string;
    lastName: string;
    title?: string;
  };
}

interface ProcessedData extends SalesByServiceProviderReportData {
  id: number;
  serviceProviderName: string;
  billCountLength: number;
}

const getFullName = (sp: SalesByServiceProviderReportData["serviceProvider"]) =>
  `${sp.title ? `${sp.title}. ` : ""}${capitalize(sp.firstName || "")} ${capitalize(
    sp.lastName || ""
  )}`.trim() || t("reports.unknownServiceProvider");

const getBillCount = (billCount: number | Set<number> | number[]) => {
  if (typeof billCount === "number") return billCount;
  if (Array.isArray(billCount)) return billCount.length;
  return (billCount as Set<number>)?.size || 0;
};

const processData = (data: SalesByServiceProviderReportData[]): ProcessedData[] =>
  data.map((item) => ({
    ...item,
    id: item.serviceProviderId,
    serviceProviderName: getFullName(item.serviceProvider),
    billCountLength: getBillCount(item.billCount)
  }));

// component to show sales by service provider report
export const SalesByServiceProviderReport = ({
  isBranchReport = false
}: {
  isBranchReport?: boolean;
}): React.JSX.Element => {
  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();
  const currentResourceCentre = useCurrentResourceCentre();

  // selectors
  const serviceProviderId = useSelector((state: RootState) =>
    state.userContext.userCreds.authenticable === "serviceProvider"
      ? state.userContext.userCreds.authenticableId
      : null
  );

  // local states
  const [reportData, setReportData] = React.useState<ProcessedData[]>([]);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [filters, setFilters] = React.useState({
    from: calFns.startOfDay(new Date()).toDate(),
    until: new Date(),
    serviceProviderIds: serviceProviderId ? [serviceProviderId] : [],
    ...(isBranchReport ? { resourceCentreIds: [currentResourceCentre?.id] } : {})
  });

  // effects
  React.useEffect(() => {
    setIsRequestLoading(true);
    reportsApi
      .salesByServiceProviderReport(filters)
      .then((data) => {
        setReportData(processData(data));
      })
      .catch(() => {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: "Failed to fetch report data.",
            autoTimeout: true
          })
        );
      })
      .finally(() => setIsRequestLoading(false));
  }, [dispatch, filters]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* filter */}
      <Box sx={{ px: 3, py: 2 }}>
        <Filters
          filters={filters as unknown as FilterProps}
          onSetFilters={(f) => setFilters(f)}
          isRequestLoading={isRequestLoading}
        />
      </Box>

      {/* list */}
      <Box
        className="salesByServiceProviderReportList"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          width: "100%",
          overflow: "auto"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "800px" : "auto", mb: "40px" }}>
          <List
            automation="salesByServiceProviderReportList"
            data={reportData}
            rowHeight={50}
            adjustHeightToContents
            defaultSortColumn="serviceProviderName"
            columns={[
              {
                key: "serviceProviderName",
                label: tl("reports.serviceProvider"),
                sortable: true,
                formatter: ({ serviceProviderName }) => (
                  <Typography>{serviceProviderName}</Typography>
                )
              },
              {
                key: "billCountLength",
                label: tl("reports.billCount"),
                sortable: true,
                formatter: ({ billCountLength }) => <Typography>{billCountLength}</Typography>
              },
              {
                key: "totalSales",
                label: tl("reports.totalSales"),
                sortable: true,
                formatter: ({ totalSales }) => <Typography>{rupeeDisplay(totalSales)}</Typography>
              }
            ]}
          >
            <EmptyView>
              <Box textAlign="center" padding="50px">
                No items to show.
              </Box>
            </EmptyView>
          </List>

          <Box className="salesByServiceProviderReportTotalBarStyle">
            <Typography className="totalTxt">{t("reports.total")}</Typography>
            <Typography sx={{ width: "150px", textAlign: "right" }}>
              {rupeeDisplay(reportData.reduce((acc, cur) => acc + (cur.totalSales || 0), 0))}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
